'use client';

import { useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { ErrorBoundary } from '@benzinga/core-ui';
import { useHydrate } from '@benzinga/hooks';
import { getRegisterType } from '@benzinga/utils';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import {
  getPremiumServiceHref,
  SiteNav,
  hidesHubSearchAndPremium,
  resolvePageContext,
  resolveNavChromeUser,
  isNavChromeLogoutPending,
  trackHubEvent,
  useNavChromeRevalidate,
  usePremiumServiceAccess,
  type HubNavUserState,
  type PremiumServiceKey,
} from '@benzinga/navigation-ui';
import { HeaderProps } from '../../../src/entities/header';
import { useNavEntity } from '../nav/NavEntityContext';

export interface RootHubNavProps {
  headerProps: HeaderProps;
}

const RootHubNav: React.FC<RootHubNavProps> = ({ headerProps }) => {
  const isLoggedInLive = useIsUserLoggedIn();
  const navChromeReady = Boolean(useHydrate(true, false));
  const logoutPending = navChromeReady && isNavChromeLogoutPending();
  const isLoggedIn = navChromeReady && isLoggedInLive && !logoutPending;
  const premiumAccess = usePremiumServiceAccess(isLoggedInLive && !logoutPending);
  const navChromeUser = logoutPending ? 'anon' : resolveNavChromeUser(isLoggedInLive, premiumAccess);
  useNavChromeRevalidate(
    navChromeUser,
    {
      edge: navChromeUser !== 'anon' && premiumAccess.hasEdge,
      pro: navChromeUser !== 'anon' && premiumAccess.hasPro,
      research: navChromeUser !== 'anon' && premiumAccess.hasResearch,
    },
    navChromeReady,
  );

  const pathname = usePathname();
  const navEntity = useNavEntity();

  const entity = navEntity?.pathname === pathname ? navEntity : null;
  const pageContext = useMemo(
    () =>
      resolvePageContext(pathname, {
        articleTicker: entity?.ticker,
        sector: entity?.sector,
        sectorSlug: entity?.sectorSlug,
      }),
    [pathname, entity],
  );

  const requestAuth = useCallback(
    (action: 'login' | 'register') => {
      const redirectUrl = new URL(
        `${window.location.origin}/login?action=${action}&next=${encodeURIComponent(window.location.href)}`,
      );
      const utmSource = getRegisterType(pathname ?? '');
      if (utmSource) {
        redirectUrl.searchParams.append('utm_source', utmSource);
      }
      window.location.href = redirectUrl.toString();
    },
    [pathname],
  );
  const onLogin = useCallback(() => requestAuth('login'), [requestAuth]);
  const onRegister = useCallback(() => requestAuth('register'), [requestAuth]);

  const onLogout = useCallback(() => {
    const current = window.location.href;
    const next = current.includes('/account') ? window.location.origin : current;
    window.location.href = `${window.location.origin}/logout?next=${encodeURIComponent(next)}`;
  }, []);

  const userState = useMemo<HubNavUserState>(() => navChromeUser, [navChromeUser]);

  const onHubOpen = useCallback(
    (hub: { key: string }) => trackHubEvent({ type: 'hub_opened', hub: hub.key, userState }),
    [userState],
  );

  const onHubLinkClick = useCallback(
    (hub: { key: string }, link: { label: string; href: string }) =>
      trackHubEvent({ type: 'hub_link_clicked', hub: hub.key, label: link.label, destination: link.href, userState }),
    [userState],
  );

  const onPremiumClick = useCallback(
    (service: PremiumServiceKey) =>
      trackHubEvent({
        type: 'hub_link_clicked',
        hub: 'premium-services',
        label: service,
        destination: getPremiumServiceHref(service, premiumAccess),
        userState,
      }),
    [premiumAccess, userState],
  );

  const onHubTitleClick = useCallback(
    (hub: { key: string; label: string; href?: string }) =>
      trackHubEvent({
        type: 'hub_link_clicked',
        hub: hub.key,
        label: hub.label,
        destination: hub.href,
        element: 'title',
        userState,
      }),
    [userState],
  );

  const onProCtaClick = useCallback(
    () =>
      trackHubEvent({
        type: 'hub_link_clicked',
        hub: 'utility-bar',
        label: 'Try Pro Free',
        destination: getPremiumServiceHref('pro', premiumAccess),
        location: 'utility_bar',
        userState,
      }),
    [premiumAccess, userState],
  );

  return (
    <ErrorBoundary name="hub-nav">
      <SiteNav
        variant="default"
        sticky={headerProps.sticky ?? true}
        pageContext={pageContext}
        premiumAccess={premiumAccess}
        isLoggedIn={isLoggedIn}
        navChromeReady={navChromeReady}
        onLogin={onLogin}
        onRegister={onRegister}
        onLogout={onLogout}
        quotes={headerProps.quotes}
        marketTickers={headerProps.marketTickers}
        initialTrendingTickers={headerProps.initialTrendingTickers}
        initialTrendingTopics={headerProps.initialTrendingTopics}
        onSectionOpen={onHubOpen}
        onSectionLinkClick={onHubLinkClick}
        onSectionTitleClick={onHubTitleClick}
        onPremiumClick={onPremiumClick}
        onProCtaClick={onProCtaClick}
        hideTickerSearch={hidesHubSearchAndPremium(pathname)}
        hidePremium={hidesHubSearchAndPremium(pathname)}
        breakingNews={headerProps.breakingNews}
        hideBanner={headerProps.hideBanner}
        showHeaderDisplayAd={headerProps.showHeaderDisplayAd ?? true}
        expandedHeaderDisplayAd={headerProps.expandedHeaderDisplayAd}
        showRotatingBanner={typeof headerProps.showRotatingBanner === 'boolean' ? headerProps.showRotatingBanner : false}
      />
    </ErrorBoundary>
  );
};

export default RootHubNav;
